import { Button, IconButton } from '@creditornot/cb-button';
import { Column } from '@creditornot/cb-data-table';
import { Download as DownloadIcon } from '@creditornot/cb-icons';
import { Empty } from '@creditornot/cb-placeholders';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { Tag, TagVariant } from '@creditornot/cb-tag';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { getDac7DownloadLink } from 'apis/merchant-gateway/insights/dac7reports/api';
import { getAllMerchantPayouts, getCombinedMerchantPayoutReport } from 'apis/merchant-payout-service/api';
import {
  AllMerchantPayouts,
  MerchantPayout,
  MerchantPayoutStatus,
  PayoutLevel,
} from 'apis/merchant-payout-service/types';
import { MerchantDetail } from 'apis/restaurant-api/merchant-admin/merchant/types';
import PageHeader from 'common/components/PageHeader';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { downloadFileFromAxiosResponse, downloadFromUrl } from 'common/utils/download';
import { countryToTimezone } from 'locale';
import { ResponsiveTable } from 'modules/experience-core';
import { useIsDac7ReportEnabled } from 'modules/merchant/hooks/useIsDac7ReportEnabled';

import { MerchantPayoutDossierModal } from './MerchantPayoutDossierModal';
import { TaxReportingAlert } from './TaxReportingAlert';
import { TaxReportingInfoModal } from './TaxReportingInfoModal';
import { useMerchantContext } from '../merchant/MerchantContext';

const EmptyMessage = styled(Empty)`
  margin-block-start: 100px;
`;

const DocumentsIcon = styled(DownloadIcon)`
  width: 16px;
  cursor: pointer;
`;

const DisabledDocumentsIcon = styled(DownloadIcon)`
  width: 16px;
  opacity: 0.5;
  pointer-events: none;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const AlertContainer = styled.div`
  margin: 16px;
`;

const Loading = () => (
  <LoadingSpinnerContainer>
    <Spinner size="large" />
  </LoadingSpinnerContainer>
);

const payoutStatuses: { [key in MerchantPayoutStatus]: string } = {
  RUNNING: 'campaigns.status.running',
  SUCCESS: 'menu.bulk-image.status-success',
  FAILED: 'menu.bulk-image.status-failure',
  NO_VENUES: 'menu.menu-sync.empty-venue-list',
  CURRENCY_MISMATCH: 'menu.bulk-image.status-failure',
  CURRENCY_VALIDATED: 'campaigns.status.running',
  PAYOUT_DISABLED: 'menu-option.disabled',
  PAYOUT_DISABLED_VALIDATED: 'campaigns.status.running',
  MISSING_VAT_ID: 'menu.bulk-image.status-failure',
  VAT_ID_VALIDATED: 'campaigns.status.running',
  KYB_PENDING: 'menu.bulk-image.status-failure',
  KYB_VALIDATED: 'campaigns.status.running',
  MISSING_CREDITOR_INFORMATION: 'menu.bulk-image.status-failure',
  BANK_INFO_VALIDATED: 'campaigns.status.running',
  MERCHANT_PAYOUT_DATA_UPDATED: 'campaigns.status.running',
  CALCULATIONS_REQUESTED: 'campaigns.status.running',
  CALCULATIONS_COMPLETED: 'campaigns.status.running',
  CALCULATIONS_FAILED: 'menu.bulk-image.status-failure',
  CALCULATIONS_RETRIEVAL_FAILED: 'menu.bulk-image.status-failure',
  COMPLETED: 'menu.bulk-image.status-success',
  INVOICES_RENDERED: 'campaigns.status.running',
  INVOICES_REQUESTED: 'campaigns.status.running',
  REPORTS_REQUESTED: 'campaigns.status.running',
  DOCUMENTS_RENDERED: 'campaigns.status.running',
  MISSING_EXTERNAL_ID: 'menu.bulk-image.status-failure',
  EMAIL_SENT_TO_MERCHANT: 'campaigns.status.running',
  RELEASED_TRANSFER: 'menu.bulk-image.status-success',
  READY_FOR_WLS_TRANSFER: 'menu.bulk-image.status-success',
  READY_FOR_WE_TRANSFER: 'menu.bulk-image.status-success',
};

const payoutStatusTagVariants: { [key in MerchantPayoutStatus]: TagVariant } = {
  RUNNING: 'lightYellow',
  SUCCESS: 'lightBlue',
  FAILED: 'lightRed',
  NO_VENUES: 'lightBlue',
  CURRENCY_MISMATCH: 'lightRed',
  CURRENCY_VALIDATED: 'lightYellow',
  MISSING_CREDITOR_INFORMATION: 'lightRed',
  BANK_INFO_VALIDATED: 'lightYellow',
  MERCHANT_PAYOUT_DATA_UPDATED: 'lightYellow',
  CALCULATIONS_REQUESTED: 'lightYellow',
  CALCULATIONS_COMPLETED: 'lightYellow',
  CALCULATIONS_FAILED: 'lightRed',
  CALCULATIONS_RETRIEVAL_FAILED: 'lightRed',
  PAYOUT_DISABLED: 'lightBlack',
  PAYOUT_DISABLED_VALIDATED: 'lightYellow',
  MISSING_VAT_ID: 'lightRed',
  VAT_ID_VALIDATED: 'lightYellow',
  KYB_PENDING: 'lightRed',
  KYB_VALIDATED: 'lightYellow',
  COMPLETED: 'lightBlue',
  INVOICES_RENDERED: 'lightYellow',
  INVOICES_REQUESTED: 'lightYellow',
  REPORTS_REQUESTED: 'lightYellow',
  DOCUMENTS_RENDERED: 'lightYellow',
  MISSING_EXTERNAL_ID: 'lightRed',
  EMAIL_SENT_TO_MERCHANT: 'lightYellow',
  RELEASED_TRANSFER: 'lightBlue',
  READY_FOR_WLS_TRANSFER: 'lightBlue',
  READY_FOR_WE_TRANSFER: 'lightBlue',
};

export const IndividualMerchantPayout = ({ merchant }: { merchant: MerchantDetail }) => {
  const intl = useIntl();
  const {
    country: merchantCountry,
    itemid: { $oid: merchantId },
  } = merchant;

  const [selectedPayout, setSelectedPayout] = useState<MerchantPayout | null>(null);

  const { data: merchantPayoutsData, isLoading } = useQuery<AllMerchantPayouts>(
    ['merchantPayoutsData', merchantId],
    () => getAllMerchantPayouts(merchantId),
  );

  const [payoutsBeingDownloaded, setPayoutsBeingDownloaded] = useState<string[]>([]);
  const [showMoreInfo, setShowMoreInfo] = React.useState(false);

  const t = (id: string) => intl.formatMessage({ id });

  const isPayoutForHRVBefore2023 = (country: string, year: number) => country === 'HRV' && year < 2023;
  const downloadArchive = (payout: MerchantPayout) => {
    setPayoutsBeingDownloaded(_ => [..._, payout.id]);

    getCombinedMerchantPayoutReport(payout.merchantId, payout.id)
      .then(downloadFileFromAxiosResponse)
      .catch(() => toast('Download failed'))
      .finally(() => setPayoutsBeingDownloaded(current => current.filter(_ => _ !== payout.id)));
  };

  const onReportDownloadStart = (payoutId: string) => {
    if (!payoutId) {
      return;
    }
    setPayoutsBeingDownloaded(_ => [..._, payoutId]);
    setSelectedPayout(null);
  };

  const onReportDownloadFinish = (payoutId: string) => {
    if (!payoutId) {
      return;
    }
    setPayoutsBeingDownloaded(current => current.filter(_ => _ !== payoutId));
  };

  const isPayoutInFailedState = (payout: MerchantPayout) =>
    [
      MerchantPayoutStatus.FAILED,
      MerchantPayoutStatus.CURRENCY_MISMATCH,
      MerchantPayoutStatus.MISSING_CREDITOR_INFORMATION,
      MerchantPayoutStatus.CALCULATIONS_FAILED,
      MerchantPayoutStatus.CALCULATIONS_RETRIEVAL_FAILED,
      MerchantPayoutStatus.MISSING_EXTERNAL_ID,
      MerchantPayoutStatus.KYB_PENDING,
      MerchantPayoutStatus.MISSING_VAT_ID,
      MerchantPayoutStatus.PAYOUT_DISABLED,
    ].includes(payout.status);

  const isPayoutInSuccessState = (payout: MerchantPayout) =>
    [
      MerchantPayoutStatus.SUCCESS,
      MerchantPayoutStatus.NO_VENUES,
      MerchantPayoutStatus.COMPLETED,
      MerchantPayoutStatus.RELEASED_TRANSFER,
      MerchantPayoutStatus.READY_FOR_WLS_TRANSFER,
      MerchantPayoutStatus.READY_FOR_WE_TRANSFER,
    ].includes(payout.status);

  const isPayoutInRunningState = (payout: MerchantPayout) =>
    !isPayoutInFailedState(payout) && !isPayoutInSuccessState(payout);
  const hasAnyMerchantLevelPayouts = (payouts: MerchantPayout[]) =>
    payouts.some(_ => _.payoutLevel === PayoutLevel.MERCHANT);
  const shouldShowTypeColumn = (merchantPayoutsData: AllMerchantPayouts) =>
    hasAnyMerchantLevelPayouts(merchantPayoutsData.payouts) && merchantPayoutsData.isOptedInMerchantLevelArchiving;
  const shouldShowDownloadColumn = (merchantPayoutsData: AllMerchantPayouts) =>
    hasAnyMerchantLevelPayouts(merchantPayoutsData.payouts) || merchantPayoutsData.isOptedInMerchantLevelArchiving;

  if (isLoading || !merchantPayoutsData) {
    return <Loading />;
  }

  const columns: Column<MerchantPayout, Record<string, unknown>>[] = [
    {
      id: '1',
      name: t('special-hours.period'),
      render: ({ record: payout }) => {
        const timezone = countryToTimezone(payout.country);
        const startDate = DateTime.fromISO(payout.interval.startDate).setZone(timezone);
        const endDate = DateTime.fromISO(payout.interval.endDate).setZone(timezone);
        return (
          <>
            {startDate.toFormat('MMM d, y')} - {endDate.toFormat('MMM d, y')}
          </>
        );
      },
    },
    {
      id: '2',
      name: t('payout-reports.amount'),
      render: ({ record: payout }) => {
        const timezone = countryToTimezone(payout.country);
        const endDateYear = DateTime.fromISO(payout.interval.endDate).setZone(timezone).year;

        return (
          <>
            {isPayoutInRunningState(payout) && (
              <Tag variant="outlineYellow" uppercase>
                CALCULATING
              </Tag>
            )}
            {isPayoutInFailedState(payout) && (
              <Tag variant="outlineRed" uppercase>
                NOT_AVAILABLE
              </Tag>
            )}
            {isPayoutInSuccessState(payout) && (
              <MonetaryAmount
                amount={
                  isPayoutForHRVBefore2023(payout.country, endDateYear)
                    ? convertMonetaryIntToDecimal(payout.payoutAmount, 'HRK')
                    : convertMonetaryIntToDecimal(payout.payoutAmount, merchantPayoutsData.currency)
                }
                currency={isPayoutForHRVBefore2023(payout.country, endDateYear) ? 'HRK' : merchantPayoutsData.currency}
              />
            )}
          </>
        );
      },
    },
    {
      id: '3',
      name: t('payout-reports.bank_account'),
      render: () => (
        <LegacyPopover
          placement="right"
          renderArrow
          content={
            <div>
              {!!merchantPayoutsData.merchantBankAccount.length && t('payout-reports.bank_account_number_tooltip')}
            </div>
          }
        >
          {!!merchantPayoutsData.merchantBankAccount.length && merchantPayoutsData.merchantBankAccount}
        </LegacyPopover>
      ),
    },
  ];

  if (shouldShowTypeColumn(merchantPayoutsData)) {
    columns.push({
      id: '4',
      name: t('campaigns.labels.type'),
      alignContent: 'left',
      render: ({ record: payout }) => (
        <>
          {payout.payoutLevel === PayoutLevel.MERCHANT && (
            <Tag variant="outlineBlue" uppercase>
              PAYOUT
            </Tag>
          )}
          {payout.payoutLevel !== PayoutLevel.MERCHANT && merchantPayoutsData.isOptedInMerchantLevelArchiving && (
            <Tag variant="outlineBlue" uppercase>
              ARCHIVE
            </Tag>
          )}
        </>
      ),
    });
  }

  columns.push({
    id: '5',
    name: t('payout-reports.status'),
    render: ({ record: payout }) => (
      <Tag variant={payoutStatusTagVariants[payout.status]} uppercase>
        {t(payoutStatuses[payout.status])}
      </Tag>
    ),
  });

  if (shouldShowDownloadColumn(merchantPayoutsData)) {
    columns.push({
      id: '6',
      name: '',
      alignContent: 'center',
      render: ({ record: payout }) => {
        const isPayoutBeingDownloaded = payoutsBeingDownloaded.includes(payout.id);

        if (payout.payoutLevel === PayoutLevel.MERCHANT) {
          return (
            <>
              {isPayoutBeingDownloaded && <Spinner size="small" color="blue" />}
              {!isPayoutBeingDownloaded && isPayoutInSuccessState(payout) && (
                <IconButton
                  variant="neutral"
                  size="small"
                  background="transparent"
                  onClick={() => setSelectedPayout(payout)}
                >
                  <DocumentsIcon />
                </IconButton>
              )}
              {!isPayoutBeingDownloaded && !isPayoutInSuccessState(payout) && <DisabledDocumentsIcon />}
            </>
          );
        }

        const isArchiveDownloadDisabled =
          payout.payoutAmount === 0 || !isPayoutInSuccessState(payout) || !payout.archived;

        return (
          <>
            {isPayoutBeingDownloaded && <Spinner size="small" color="blue" />}
            {!isPayoutBeingDownloaded && !isArchiveDownloadDisabled && (
              <DocumentsIcon onClick={() => downloadArchive(payout)} />
            )}
            {!isPayoutBeingDownloaded && isArchiveDownloadDisabled && <DisabledDocumentsIcon />}
          </>
        );
      },
    });
  }
  const handleModalClose = () => {
    setShowMoreInfo(false);
  };

  return (
    <>
      <AlertContainer>
        <TaxReportingAlert merchantId={merchantId} country={merchantCountry} isRoot={false} />
      </AlertContainer>
      {showMoreInfo && <TaxReportingInfoModal merchantId={merchantId} onClose={handleModalClose} />}
      {merchantPayoutsData.payouts.length === 0 && (
        <EmptyMessage title={intl.formatMessage({ id: 'purchases.messages.no-items' })} />
      )}
      {merchantPayoutsData.payouts.length !== 0 && (
        <ResponsiveTable<MerchantPayout>
          dataSource={merchantPayoutsData.payouts}
          columns={columns}
          getRowKey={payout => payout.id}
          loading={!merchantPayoutsData}
          emptyMessage={{
            title: t('state.error'),
            description: t('errors.unknown-error-retry-contact'),
          }}
          horizontalPadding="wide"
        />
      )}
      {!!selectedPayout && selectedPayout.dossier && (
        <MerchantPayoutDossierModal
          merchantId={selectedPayout.merchantId}
          intl={intl}
          interval={selectedPayout.interval}
          dossier={selectedPayout.dossier}
          onClose={() => setSelectedPayout(null)}
          onDownloadStart={onReportDownloadStart}
          onDownloadFinish={onReportDownloadFinish}
        />
      )}
    </>
  );
};

const MerchantPayouts = () => {
  const { merchant } = useMerchantContext();
  const { formatMessage } = useIntl();
  const {
    country: merchantCountry,
    itemid: { $oid: merchantId },
  } = merchant;

  const isDac7Enabled = useIsDac7ReportEnabled({ merchantId, merchantCountry });

  const { data: dac7ReportLink } = useQuery(
    ['getDac7DownloadLink', merchantId],
    () => getDac7DownloadLink(merchantId),
    { enabled: isDac7Enabled },
  );

  return (
    <Root>
      <PageHeader title={formatMessage({ id: 'navigation.payout-reports' })}>
        {!!dac7ReportLink && (
          <Button size="small" variant="primary" onClick={() => downloadFromUrl(dac7ReportLink)}>
            {formatMessage({ id: 'payout-reports.download-dac-7-2023-report' })}
          </Button>
        )}
      </PageHeader>
      <IndividualMerchantPayout merchant={merchant} />
    </Root>
  );
};

export default MerchantPayouts;
